import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const card = "rounded-2xl border border-[#0B2A4A]/10 bg-white p-6";

function TopThree({ items }) {
  return (
    <ol className="mt-4 grid sm:grid-cols-3 gap-3" data-testid="questionnaire-top3">
      {items.map((r, i) => (
        <li key={r.number} className="rounded-xl bg-[#0B2A4A] text-[#F5ECD7] p-4" data-testid={`questionnaire-top-${i + 1}`}>
          <p className="font-mono-ui text-[11px] tracking-widest text-[#F08A3C]">#{i + 1} · AVENUE {r.number}</p>
          <p className="mt-1 font-display text-lg">{r.avenue}</p>
          <p className="mt-1 text-sm text-[#F5ECD7]/80">{r.match}% match</p>
        </li>
      ))}
    </ol>
  );
}

export function StudentQuestionnaireReveal() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/course/questionnaire-result").then((x) => setR(x.data)).catch(() => {}); }, []);
  if (!r) return null;
  if (!r.revealed)
    return (
      <div className={`${card} flex gap-3`} data-testid="questionnaire-sealed">
        <Lock className="h-5 w-5 text-[#F08A3C] shrink-0 mt-0.5" />
        <div><p className="font-semibold text-[#0B2A4A]">Your questionnaire result is still sealed</p>
          <p className="mt-1 text-sm text-slate-600">It opens once Day 5 is complete or when Greg opens it in class. Answered {r.answered} of {r.total} questions.</p></div>
      </div>
    );
  return (
    <div className={card} data-testid="questionnaire-revealed">
      <p className="text-[11px] font-bold uppercase tracking-widest text-[#D96F28] flex items-center gap-1.5"><Sparkles className="h-4 w-4" /> Profile Questionnaire · your second opinion</p>
      {r.top3.length ? <TopThree items={r.top3} /> : <p className="mt-3 text-sm text-slate-600" data-testid="questionnaire-incomplete">Answer all {r.total} questions in <Link to="/course/day/1" className="font-semibold text-[#147C88]">Day 1</Link> to see your top 3 ({r.answered} done).</p>}
      <p className="mt-4 text-sm text-slate-600">This is a second opinion, not a verdict. Compare it with the top 2 you chose yourself on Day 5.</p>
    </div>
  );
}

export function HostQuestionnaire({ userId }) {
  const [r, setR] = useState(null);
  const load = () => api.get(`/admin/students/${userId}/questionnaire`).then((x) => setR(x.data)).catch(() => {});
  useEffect(() => { load(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps
  const setReveal = (reveal) => api.put(`/admin/students/${userId}/questionnaire-reveal`, { reveal }).then((x) => { setR(x.data); toast.success(reveal ? "Result opened for the student" : reveal === false ? "Result sealed" : "Back to automatic (opens after Day 5)"); }).catch((e) => toast.error(formatApiError(e)));
  if (!r) return null;
  const btn = "rounded-lg border border-[#0B2A4A]/20 px-3 py-1.5 text-xs font-semibold hover:border-[#1A9AA8]";
  return (
    <div className={card} data-testid="host-questionnaire-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><h3 className="font-semibold text-[#0B2A4A]">Host only · Profile Questionnaire score</h3>
          <p className="mt-1 text-sm text-slate-600" data-testid="host-questionnaire-status">{r.answered}/{r.total} answered · {r.revealed ? "Open to student" : "Sealed from student"}{r.reveal_override === null || r.reveal_override === undefined ? " (auto: opens when Day 5 is complete)" : " (set by you)"}</p></div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setReveal(true)} className={btn} data-testid="questionnaire-reveal-btn">Open now</button>
          <button onClick={() => setReveal(false)} className={btn} data-testid="questionnaire-seal-btn">Keep sealed</button>
          <button onClick={() => setReveal(null)} className={btn} data-testid="questionnaire-auto-btn">Automatic</button>
        </div>
      </div>
      {r.top3.length ? <TopThree items={r.top3} /> : <p className="mt-3 text-sm text-slate-500" data-testid="host-questionnaire-incomplete">Top 3 appears once all {r.total} questions are answered.</p>}
      {r.ranking.length > 0 && (
        <table className="mt-5 w-full text-sm" data-testid="host-questionnaire-ranking">
          <thead><tr className="text-left text-slate-500"><th className="py-1">#</th><th>Avenue</th><th>Points</th><th>Match</th></tr></thead>
          <tbody>{r.ranking.map((x) => <tr key={x.number} className="border-t border-[#0B2A4A]/5"><td className="py-1.5 font-mono-ui">{x.number}</td><td>{x.avenue}</td><td>{x.points}/{x.max}</td><td>{x.match}%</td></tr>)}</tbody>
        </table>
      )}
    </div>
  );
}
